/**
 * homepageSeo — ONE metadata authority for the public homepage, whichever
 * version is rendered there.
 *
 * TREATX-SEO-RECOVERY-S1 (2026-06):
 *   The audit found that `/` emitted complete metadata only because V1
 *   happened to carry its own <SEO> block, while V2 and V3 carried none.
 *   Publishing V3 would therefore have silently dropped the homepage title,
 *   description, canonical, Open Graph/Twitter card and Organization JSON-LD.
 *
 *   The values below are the ESTABLISHED homepage metadata, lifted verbatim
 *   from the V1 implementation — not new copy, and not a new CMS schema. The
 *   Landing CMS has no SEO fields (`landing_cms_service` stores section copy
 *   only), so there is nothing to read; when SEO fields are introduced there,
 *   this module is the single place that consumes them.
 *
 *   Consumed by LandingPage (V1), LandingPageV2 and LandingPageV3 so all three
 *   produce identical head output at `/`. No page keeps a private copy.
 */
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// The only canonical homepage URL. `/v1`, `/v2` and `/v3` are previews of this
// URL, never alternative homepages, so they canonicalise here.
export const HOMEPAGE_CANONICAL_PATH = '/';

export const HOMEPAGE_SEO = {
  title: 'Verified Medical Treatments Worldwide',
  description:
    'Compare verified clinics, get personalized quotes, and save on medical treatments abroad with TreatX — trusted providers, transparent pricing, escrow-protected payments.',
  jsonLd: {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'TreatX',
    url: 'https://treatx.health',
    logo: 'https://treatx.health/favicon.svg',
    sameAs: [],
  },
};

// og:locale per served language. Anything else falls back to the global surface.
const OG_LOCALES = { en: 'en_US', tr: 'tr_TR' };

/**
 * Head props for whichever homepage version is mounted.
 *
 * Indexability is decided by WHERE the component renders, not by which version
 * it is: only the published canonical homepage (`/`) is indexable. The version
 * preview routes and the admin draft preview render the same page with
 * `noindex` so they can never compete with `/` in the index.
 */
export function useHomepageSeoProps({ draftPreview = false } = {}) {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const lang = (i18n?.language || 'en').split('-')[0];
  const isPublishedHomepage = !draftPreview && pathname === HOMEPAGE_CANONICAL_PATH;

  return {
    ...HOMEPAGE_SEO,
    // The admin draft preview is an authenticated admin screen, not a public
    // URL, so it declares no canonical at all.
    canonicalPath: draftPreview ? undefined : HOMEPAGE_CANONICAL_PATH,
    noindex: !isPublishedHomepage,
    lang,
    locale: OG_LOCALES[lang] || OG_LOCALES.en,
  };
}
